import React from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydCheckbox from '../../../../commonDesign/SydCheckbox'
import { IconPickerField } from '../../../../commonDesign/IconPicker'
import { DEFAULT_ENDPOINTS, DEFAULT_POLICY, DEFAULT_TAG } from './constants'

const styleVariantOptions = [
  { label: 'Small', value: 'sm' },
  { label: 'Medium (Default)', value: 'md' },
  { label: 'Large', value: 'lg' }
]

const sectionHeading = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  borderBottom: '1px solid var(--color-neutral-200)',
  paddingBottom: '4px'
}

function WoSfTaskListConfigEditor ({ fieldPrefix }) {
  const { control } = useFormContext()
  const configPrefix = `${fieldPrefix}.config`

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <div style={sectionHeading}>Appearance</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.title`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Title' helperText='Override the default title'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Action Items'
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.icon`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Icon' helperText='Icon name'>
              <IconPickerField
                value={field.value || null}
                onChange={field.onChange}
                placeholder='Select an icon...'
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${fieldPrefix}.styleVariant`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Size' helperText='Component size variant'>
              <SydSelect
                options={styleVariantOptions}
                value={field.value || 'md'}
                onChange={(e) => field.onChange(e.target.value)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Filtering</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.completedFilter`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Completed Filter' helperText='Which tasks to display'>
              <SydSelect
                options={[
                  { label: 'Open Only', value: 'open' },
                  { label: 'Completed Only', value: 'completed' },
                  { label: 'All', value: 'all' }
                ]}
                value={field.value || 'all'}
                onChange={(e) => field.onChange(e.target.value || null)}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.limit`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Limit' helperText='Max tasks to display (blank = all)'>
              <SydInput
                type='number'
                value={field.value ?? ''}
                onChange={(e) => {
                  const raw = e.target.value
                  if (!raw || !raw.trim()) { field.onChange(null); return }
                  const parsed = parseInt(raw, 10)
                  field.onChange(isNaN(parsed) ? null : parsed)
                }}
                placeholder='All'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Smart Endpoint Overrides</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.listEndpoint`}
          control={control}
          render={({ field }) => (
            <SydLabel label='List Endpoint' helperText='SE base name for task list'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder={DEFAULT_ENDPOINTS.list}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.markEndpoint`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Mark Endpoint' helperText='SE base name for mark complete'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder={DEFAULT_ENDPOINTS.mark}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.policy`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Policy' helperText='Production policy segment'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder={DEFAULT_POLICY}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.tag`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Tag' helperText='Production tag segment'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder={DEFAULT_TAG}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.devPolicy`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Dev Policy' helperText='Initial debug policy (defaults to Policy)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Same as Policy'
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.devTag`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Dev Tag' helperText='Initial debug tag (defaults to Tag)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Same as Tag'
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.showDebugControl`}
          control={control}
          render={({ field }) => (
            <SydCheckbox
              label='Show debug control for Summit users (shown by default)'
              size='small'
              value={field.value !== false}
              onChange={(_e, checked) => field.onChange(checked)}
            />
          )}
        />
      </Grid>
    </Grid>
  )
}

WoSfTaskListConfigEditor.propTypes = {
  fieldPrefix: PropTypes.string.isRequired
}

export default WoSfTaskListConfigEditor
